import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Slider from '@mui/material/Slider';

const CARRIER_COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#0097a7', '#c62828', '#2e7d32', '#e65100', '#4527a0',
];

// Sentinel values that represent unknown/unavailable cellular data
const UNKNOWN_KEYS = new Set(['2147483647', '[]']);

function normalizeKey(key: string): string {
  return UNKNOWN_KEYS.has(key) ? 'Unknown' : key;
}

interface BandPciHistogramProps {
  carriers: string[];
  lteBandCounts: Record<string, Record<string, number>>;
  ltePciCounts: Record<string, Record<string, number>>;
  nrBandCounts: Record<string, Record<string, number>>;
  nrPciCounts: Record<string, Record<string, number>>;
}

type ViewMode = 'band' | 'pci';

function buildChartData(
  counts: Record<string, Record<string, number>>,
  carriers: string[],
  topN: number,
  showUnknown: boolean,
): Record<string, string | number>[] {
  // Aggregate counts across all carriers, normalizing unknown sentinel values
  const totals: Record<string, number> = {};
  carriers.forEach(carrier => {
    Object.entries(counts[carrier] ?? {}).forEach(([key, count]) => {
      const normalized = normalizeKey(key);
      totals[normalized] = (totals[normalized] ?? 0) + count;
    });
  });

  let topKeys = Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([key]) => key);

  if (!showUnknown) {
    topKeys = topKeys.filter(k => k !== 'Unknown');
  }

  return topKeys.map(key => {
    const entry: Record<string, string | number> = { name: key };
    carriers.forEach(carrier => {
      if (key === 'Unknown') {
        // Sum all unknown sentinel values for this carrier
        const val = Object.entries(counts[carrier] ?? {}).reduce(
          (sum, [k, v]) => sum + (UNKNOWN_KEYS.has(k) ? v : 0),
          0,
        );
        entry[carrier] = val;
      } else {
        entry[carrier] = counts[carrier]?.[key] ?? 0;
      }
    });
    return entry;
  });
}

function SectionChart({
  title,
  data,
  carriers,
}: {
  title: string;
  data: Record<string, string | number>[];
  carriers: string[];
}) {
  if (data.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" mb={1}>{title}</Typography>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          {carriers.map((carrier, i) => (
            <Bar key={carrier} dataKey={carrier} fill={CARRIER_COLORS[i % CARRIER_COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default function BandPciHistogram({
  carriers,
  lteBandCounts,
  ltePciCounts,
  nrBandCounts,
  nrPciCounts,
}: BandPciHistogramProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('band');
  const [topN, setTopN] = useState(5);
  const [showUnknown, setShowUnknown] = useState(false); // hidden by default; toggled via "Show Unknown" button

  const lteCounts = viewMode === 'band' ? lteBandCounts : ltePciCounts;
  const nrCounts = viewMode === 'band' ? nrBandCounts : nrPciCounts;

  const lteData = useMemo(
    () => buildChartData(lteCounts, carriers, topN, showUnknown),
    [lteCounts, carriers, topN, showUnknown],
  );
  const nrData = useMemo(
    () => buildChartData(nrCounts, carriers, topN, showUnknown),
    [nrCounts, carriers, topN, showUnknown],
  );

  const lteLabel = viewMode === 'band' ? 'LTE 4G — Top Bands' : 'LTE 4G — Top PCIs';
  const nrLabel = viewMode === 'band' ? 'NR 5G — Top Bands' : 'NR 5G — Top PCIs';

  return (
    <Box>
      <Typography variant="h6" mb={2}>Band / PCI Distribution by Carrier</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
        >
          <ToggleButton value="band">By Band</ToggleButton>
          <ToggleButton value="pci">By PCI</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 240 }}>
          <Typography variant="body2" whiteSpace="nowrap">Top {topN}</Typography>
          <Slider
            value={topN}
            min={3}
            max={20}
            step={1}
            onChange={(_, v) => setTopN(v as number)}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>
        <ToggleButton
          value="showUnknown"
          selected={showUnknown}
          onChange={() => setShowUnknown(prev => !prev)}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Show Unknown
        </ToggleButton>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <SectionChart title={lteLabel} data={lteData} carriers={carriers} />
        <SectionChart title={nrLabel} data={nrData} carriers={carriers} />
      </Box>
    </Box>
  );
}
